import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'
import { useUserStore } from './user'

export interface LoginCredentials {
  usernameOrEmail: string
  password: string
}

export interface AuthResponse {
  user: any
  token: string
}

/**
 * DD-1470 — login() used to resolve to a plain boolean. Now the backend can also respond with
 * `requires2FA: true` (2fa enrolled from the product, honored here for the first time) instead
 * of a token — that's not a failure, it's an intermediate step, so it can't collapse into `false`
 * the way an actual login error does (the UI needs to tell them apart to know whether to show
 * the code input or the error snackbar).
 */
export interface LoginResult {
  ok: boolean
  requires2FA?: boolean
  twoFactorMethod?: 'email' | 'totp'
  twoFactorToken?: string
  /** DD-1771 — 2FA obligatorio sin configurar: hay que enrolarse antes de entrar. */
  requiresTwoFactorSetup?: boolean
  setupToken?: string
}

/** DD-1771 — resultado del paso 1 del enrolamiento forzado. `expired`: hay que volver al login. */
export type EnrollSetupResult =
  | { ok: true; method: 'totp'; qrCode: string; manualEntryKey: string }
  | { ok: true; method: 'email'; codeSent: true }
  | { ok: false; expired?: true }

export interface EnrollVerifyResult {
  ok: boolean
  expired?: true
}

const ENROLL_EXPIRED_MESSAGE = 'La configuración de 2FA venció. Iniciá sesión de nuevo.'

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore()
  
  // State
  const storedToken = localStorage.getItem('auth_token')
  const token = ref<string | null>(storedToken)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const initializing = ref(false)
  // DD-1771 — token de corta vida del enrolamiento forzado de 2FA. No es una sesión: sólo vive en
  // memoria (no en localStorage) y sólo sirve para /auth/2fa/enroll/*.
  const setupToken = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => {
    const hasToken = !!token.value
    const hasUser = !!userStore.user
    return hasToken && hasUser
  })
  
  const hasToken = computed(() => !!token.value)

  // Simple authentication check
  const checkAuth = () => {
    const token = localStorage.getItem('auth_token')
    return !!token
  }

  // Actions
  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.post('/auth/login', credentials)

      // DD-1771: 2FA obligatorio sin configurar — paso intermedio, tampoco es un error.
      if (response.data?.requiresTwoFactorSetup) {
        setupToken.value = response.data.setupToken
        return {
          ok: false,
          requiresTwoFactorSetup: true,
          setupToken: response.data.setupToken,
        }
      }

      // DD-1470: second step required — not an error, don't touch token/error state.
      if (response.data?.requires2FA) {
        return {
          ok: false,
          requires2FA: true,
          twoFactorMethod: response.data.twoFactorMethod,
          twoFactorToken: response.data.twoFactorToken,
        }
      }

      const { user, token: authToken } = response.data.data

      // Store token
      token.value = authToken
      localStorage.setItem('auth_token', authToken)

      // Set user from login response, then hydrate privileges via /auth/profile
      userStore.setUser(user)
      await getProfile()

      return { ok: true }
    } catch (err: any) {
      // Handle different types of errors
      if (err.response?.status === 423) {
        error.value = err.response?.data?.message || 'Cuenta bloqueada temporalmente. Intenta nuevamente más tarde.'
      } else if (err.response?.status === 401) {
        error.value = 'Credenciales inválidas. Por favor verifica tu usuario y contraseña.'
      } else if (err.response?.status === 422) {
        error.value = err.response?.data?.message || 'Datos de entrada inválidos'
      } else if (err.response?.status >= 500) {
        error.value = 'Error del servidor. Por favor intenta más tarde.'
      } else {
        error.value = err.response?.data?.message || 'Error al iniciar sesión'
      }
      return { ok: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * DD-1470 — second step of login when the first step returned requires2FA. Same shape/handling
   * as `login()`'s success path (store token, set user, hydrate profile).
   */
  const verifyTwoFactor = async (twoFactorToken: string, code: string): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.post('/auth/verify-2fa', { twoFactorToken, code })
      const { user, token: authToken } = response.data.data

      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      userStore.setUser(user)
      await getProfile()

      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Código de verificación inválido'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * DD-1771 — enrolamiento forzado, paso 1: elegir método con el setupToken del login. TOTP
   * devuelve QR + clave manual; email, que el código se envió.
   */
  const enrollSetup = async (method: 'totp' | 'email'): Promise<EnrollSetupResult> => {
    if (!setupToken.value) {
      error.value = ENROLL_EXPIRED_MESSAGE
      return { ok: false, expired: true }
    }

    loading.value = true
    error.value = null

    try {
      const response = await apiService.post('/auth/2fa/enroll/setup', { setupToken: setupToken.value, method })
      const data = response.data
      if (data.method === 'totp') {
        return { ok: true, method: 'totp', qrCode: data.qrCode, manualEntryKey: data.manualEntryKey }
      }
      return { ok: true, method: 'email', codeSent: true }
    } catch (err: any) {
      if (err.response?.data?.code === 'SETUP_EXPIRED') {
        setupToken.value = null
        error.value = ENROLL_EXPIRED_MESSAGE
        return { ok: false, expired: true }
      }
      error.value = err.response?.data?.message || 'No se pudo iniciar la configuración de 2FA'
      return { ok: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * DD-1771 — enrolamiento forzado, paso 2: verificar el código. OK → mismo camino que
   * verifyTwoFactor() (guarda el token, setea el usuario, hidrata el perfil).
   */
  const enrollVerify = async (code: string): Promise<EnrollVerifyResult> => {
    if (!setupToken.value) {
      error.value = ENROLL_EXPIRED_MESSAGE
      return { ok: false, expired: true }
    }

    loading.value = true
    error.value = null

    try {
      const response = await apiService.post('/auth/2fa/enroll/verify', { setupToken: setupToken.value, code })
      const { user, token: authToken } = response.data.data

      setupToken.value = null
      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      userStore.setUser(user)
      await getProfile()

      return { ok: true }
    } catch (err: any) {
      if (err.response?.data?.code === 'SETUP_EXPIRED') {
        setupToken.value = null
        error.value = ENROLL_EXPIRED_MESSAGE
        return { ok: false, expired: true }
      }
      error.value = 'Código de verificación inválido'
      return { ok: false }
    } finally {
      loading.value = false
    }
  }

  /** DD-1771 — "Volver al login" desde el enrolamiento forzado: descarta el setupToken. */
  const cancelEnrollment = () => {
    setupToken.value = null
    error.value = null
  }

  const logout = async (): Promise<void> => {
    try {
      // Call logout endpoint (optional, for server-side cleanup)
      await apiService.post('/auth/logout')
    } catch (error) {
      // Ignore logout errors
      console.warn('Logout endpoint error:', error)
    } finally {
      // Clear local state
      token.value = null
      localStorage.removeItem('auth_token')
      userStore.logout()
    }
  }

  const refreshToken = async (): Promise<boolean> => {
    if (!token.value) return false
    
    try {
      const response = await apiService.post('/auth/refresh-token')
      const { user, token: newToken } = response.data.data
      
      // Update token
      token.value = newToken
      localStorage.setItem('auth_token', newToken)
      
      // Update user store
      userStore.setUser(user)
      
      return true
    } catch (error) {
      // Token refresh failed, logout user
      await logout()
      return false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    loading.value = true
    error.value = null
    
    try {
      await apiService.post('/auth/change-password', {
        currentPassword,
        newPassword
      })
      
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Password change failed'
      return false
    } finally {
      loading.value = false
    }
  }

  // DD-1782: se eliminaron requestPasswordReset / resetPassword. Pegaban a los endpoints públicos
  // /auth/forgot-password y /auth/reset-password (ya removidos del server por permitir account
  // takeover). No los usaba ninguna vista fuera del diálogo muerto de Login.vue.

  const getProfile = async (): Promise<boolean> => {
    try {
      const response = await apiService.get('/auth/profile')
      
      if (response.data && response.data.data) {
        userStore.setUser(response.data.data)
        return true
      } else {
        console.error('getProfile - invalid response format:', response.data)
        return false
      }
    } catch (error: any) {
      console.error('getProfile - failed:', error)
      console.error('getProfile - error response:', error.response?.data)
      console.error('getProfile - error status:', error.response?.status)
      
      // Only clear token if it's a real authentication error (401)
      // Don't clear token for server errors (500, 502, 503, etc.)
      if (error.response?.status === 401) {
        token.value = null
        localStorage.removeItem('auth_token')
        userStore.logout()
      } else if (error.response?.status >= 500) {
        // Don't clear token for server errors
      } else {
        // Don't clear token for other errors
      }
      
      return false
    }
  }

  const updateProfile = async (profileData: any): Promise<boolean> => {
    loading.value = true
    error.value = null
    
    try {
      const response = await apiService.put('/auth/profile', profileData)
      userStore.setUser(response.data.data)
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Profile update failed'
      return false
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  // Check if server is available
  const checkServerHealth = async (): Promise<boolean> => {
    try {
      const response = await fetch('/health', { 
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      
      // Consider server available if we get any response (even 500)
      return response.status >= 200 && response.status < 600
    } catch (error) {
      return false
    }
  }

  // Initialize auth state from localStorage
  const initializeAuth = async () => {
    if (initialized.value || initializing.value) {
      return isAuthenticated.value
    }
    
    initializing.value = true
    
    try {
      if (token.value) {
        // Check if server is available first
        const serverAvailable = await checkServerHealth()
        
        if (!serverAvailable) {
          initialized.value = true
          return true // Keep the token if server is not available
        }
        
        // Try to get user profile to validate token
        const success = await getProfile()
        
        if (!success) {
          // Check if the failure was due to server errors (not auth errors)
          // In this case, we'll keep the token and assume the user is authenticated
          // This prevents users from being logged out due to server problems
          initialized.value = true
          return true
        } else {
          initialized.value = true
          return true
        }
      } else {
        const storedToken = localStorage.getItem('auth_token')
        if (storedToken) {
          token.value = storedToken
          return await initializeAuth() // Recursive call to validate the token
        } else {
          initialized.value = true
          return false
        }
      }
    } finally {
      initializing.value = false
    }
  }

  return {
    // State
    token,
    loading,
    error,
    initialized,
    initializing,
    setupToken,

    // Computed
    isAuthenticated,
    hasToken,

    // Actions
    login,
    verifyTwoFactor,
    enrollSetup,
    enrollVerify,
    cancelEnrollment,
    logout,
    refreshToken,
    changePassword,
    getProfile,
    updateProfile,
    clearError,
    initializeAuth,
    checkServerHealth,
    checkAuth
  }
})
